import {useMemo, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Layout} from '@/components/Layout';
import {Card} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Textarea} from '@/components/ui/textarea';
import {Input} from '@/components/ui/input';
import {Badge} from '@/components/ui/badge';
import {Label} from '@/components/ui/label';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {toast} from '@/hooks/use-toast';
import {shelterSignupsApi, type ShelterSignupRequest, type ShelterSignupStatus} from './api';

const STATUS_LABELS: Record<ShelterSignupStatus, string> = {
  new: 'Nuevo',
  in_review: 'En revisión',
  approved: 'Aprobado',
  rejected: 'Reprobado',
};

export default function ShelterSignupRequestsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | ShelterSignupStatus>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ShelterSignupRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);

  const {data, isLoading} = useQuery({
    queryKey: ['shelter-signups', statusFilter],
    queryFn: () => shelterSignupsApi.listAdmin(statusFilter === 'all' ? undefined : {status: statusFilter}),
  });

  const updateMutation = useMutation({
    mutationFn: ({id, status, rejectionReason}: {id: number; status: ShelterSignupStatus; rejectionReason?: string | null}) =>
      shelterSignupsApi.updateStatus(id, {status, rejectionReason}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['shelter-signups']});
      queryClient.invalidateQueries({queryKey: ['shelter-signups-new-count']});
      toast({title: 'Estado actualizado'});
      setRejectOpen(false);
      setRejectReason('');
    },
    onError: (error: any) => {
      toast({variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudo actualizar la solicitud.'});
    },
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data || []).filter((item) => {
      if (!q) return true;
      return [
        item.shelterName,
        item.contactName,
        item.contactEmail,
        item.contactPhone,
        item.city?.name,
        item.city?.department,
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [data, search]);

  const formatDateTime = (value?: string) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const badgeClass = (status: ShelterSignupStatus) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_review': return 'bg-amber-100 text-amber-800';
      case 'approved': return 'bg-emerald-100 text-emerald-800';
      case 'rejected': return 'bg-rose-100 text-rose-800';
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Interés de entidades</h1>
          <p className="text-sm text-muted-foreground">Solicitudes de instituciones o refugios que quieren anunciar sus animales.</p>
        </div>

        <Card className="p-4 flex flex-col gap-3 md:flex-row">
          <Input
            placeholder="Buscar por refugio, responsable, email o ciudad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="md:max-w-sm"
          />
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as any)}>
            <SelectTrigger className="md:w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="new">Nuevo</SelectItem>
              <SelectItem value="in_review">En revisión</SelectItem>
              <SelectItem value="approved">Aprobado</SelectItem>
              <SelectItem value="rejected">Reprobado</SelectItem>
            </SelectContent>
          </Select>
        </Card>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Refugio</TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Cargando...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No hay solicitudes.</TableCell></TableRow>
              ) : rows.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.shelterName}</TableCell>
                  <TableCell>
                    <div>{item.contactName}</div>
                    <div className="text-xs text-muted-foreground">{item.contactEmail}</div>
                    <div className="text-xs text-muted-foreground">{item.contactPhone}</div>
                  </TableCell>
                  <TableCell>{item.city?.name}{item.city?.department ? ` - ${item.city.department}` : ''}</TableCell>
                  <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  <TableCell><Badge className={badgeClass(item.status)}>{STATUS_LABELS[item.status]}</Badge></TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => setSelected(item)}>Ver detalle</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Solicitud #{selected?.id}</DialogTitle></DialogHeader>
            {selected && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Field label="Refugio" value={selected.shelterName} />
                  <Field label="Responsable" value={selected.contactName} />
                  <Field label="Celular" value={selected.contactPhone} />
                  <Field label="Email" value={selected.contactEmail} />
                  <Field label="Ciudad" value={`${selected.city?.name || '—'}${selected.city?.department ? ` - ${selected.city.department}` : ''}`} />
                  <Field label="Fecha" value={formatDateTime(selected.createdAt)} />
                  <Field label="Estado" value={STATUS_LABELS[selected.status]} />
                  <Field label="Revisado por" value={selected.reviewedByUser?.fullName || selected.reviewedByUser?.email || '—'} />
                  {selected.convertedShelter && <Field label="Entidad creada" value={`${selected.convertedShelter.name || 'Entidad'} (#${selected.convertedShelter.id})`} />}
                  {selected.rejectionReason && <Field label="Motivo de rechazo" value={selected.rejectionReason} />}
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => updateMutation.mutate({id: selected.id, status: 'in_review'})}
                    disabled={updateMutation.isPending || selected.status === 'approved' || selected.status === 'in_review'}
                  >
                    Marcar en revisión
                  </Button>
                  <Button
                    onClick={() => updateMutation.mutate({id: selected.id, status: 'approved'})}
                    disabled={updateMutation.isPending || selected.status === 'approved'}
                  >
                    Aprobar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setRejectReason(selected.rejectionReason || '');
                      setRejectOpen(true);
                    }}
                    disabled={updateMutation.isPending || selected.status === 'approved'}
                  >
                    Reprobar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Motivo del rechazo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancelar</Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (!selected) return;
                    updateMutation.mutate({id: selected.id, status: 'rejected', rejectionReason: rejectReason});
                  }}
                  disabled={updateMutation.isPending || !rejectReason.trim()}
                >
                  Confirmar rechazo
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function Field({label, value}: {label: string; value: string}) {
  return <div><p className="text-muted-foreground">{label}</p><p className="font-medium">{value}</p></div>;
}
